import { useCallback } from 'react';

import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { UserRejectedRequestError, BaseError } from 'viem';
import { useWriteContract, usePublicClient } from 'wagmi';

import { predictionContractAbi } from '@/abi';
import { useRouter } from '@/i18n/navigation';
import { T_UserBlockchainData } from '@/ts/types/T_UserBlockchainData';

interface I_UseClaimWinningsProps {
  isClaiming: boolean;
  setIsClaiming: (value: boolean) => void;
  setClaimingParticipationId: (id: string | null) => void;
}

export function useClaimWinnings({
  isClaiming,
  setIsClaiming,
  setClaimingParticipationId
}: I_UseClaimWinningsProps) {
  const t = useTranslations();
  const router = useRouter();
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();

  const handleClaimWin = useCallback(
    async (
      participation: T_UserBlockchainData['predictionEventParticipations'][0]
    ) => {
      if (
        !participation.claimData ||
        !participation.predictionEvent.predictionContract.contractAddress ||
        isClaiming
      ) {
        return;
      }

      const { claimData } = participation;
      const { predictionId, rewardAmount, merkleProofs } = claimData;

      try {
        setIsClaiming(true);
        setClaimingParticipationId(participation.id);

        // Convert merkleProofs strings to bytes32 array
        const merkleProof = merkleProofs.map(
          (proof: string) => proof as `0x${string}`
        );

        const txHash = await writeContractAsync({
          address: participation.predictionEvent.predictionContract
            .contractAddress as `0x${string}`,
          abi: predictionContractAbi,
          functionName: 'claimWinnings',
          args: [BigInt(predictionId), BigInt(rewardAmount), merkleProof]
        });

        if (publicClient) {
          await publicClient.waitForTransactionReceipt({
            hash: txHash,
            confirmations: 2
          });
        }

        toast.success(
          t('profile.predictionWinners.claimSuccess') ||
            'Successfully claimed winnings!'
        );

        // Optionally refresh the page or refetch data
        router.refresh();
      } catch (error) {
        const isUserRejection =
          error instanceof UserRejectedRequestError ||
          (error instanceof BaseError &&
            String(error.cause).toLowerCase().includes('user rejected'));

        if (!isUserRejection) {
          toast.error(
            t('profile.predictionWinners.claimError') ||
              'Failed to claim winnings'
          );
        }
        console.error('Claim winnings error:', error);
      } finally {
        setIsClaiming(false);
        setClaimingParticipationId(null);
      }
    },
    [
      isClaiming,
      setIsClaiming,
      setClaimingParticipationId,
      writeContractAsync,
      publicClient,
      router,
      t
    ]
  );

  return { handleClaimWin };
}
